import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Spinner,
  Card,
  Modal,
  Row,
  Col,
  Pagination,
  Badge,
  InputGroup,
  Form,
} from "react-bootstrap";
import {
  Eye,
  Telephone,
  Envelope,
  Calendar,
  Search,
  ArrowClockwise,
} from "react-bootstrap-icons";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE = "http://34.206.193.218:4001/api/admin";

const VendorManagement = () => {
  const [vendors, setVendors] = useState([]);
  const [filteredVendors, setFilteredVendors] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");

  const [selectedVendor, setSelectedVendor] = useState(null);
  const [showView, setShowView] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 10;

  const token =
    localStorage.getItem("adminToken") || localStorage.getItem("token");

  /* ================= FETCH VENDORS ================= */
  const fetchVendors = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/vendors/getAll`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page: currentPage, limit },
      });

      if (res.data.success) {
        setVendors(res.data.data || []);
        setFilteredVendors(res.data.data || []);
        setTotalPages(res.data.totalPages || 1);
      }
    } catch {
      toast.error("Failed to load vendors");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVendors();
  }, [currentPage]);

  /* ================= SEARCH ================= */
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredVendors(vendors);
      return;
    }

    const lower = searchTerm.toLowerCase();

    const filtered = vendors.filter(
      (v) =>
        v.companyName?.toLowerCase().includes(lower) ||
        v.email?.toLowerCase().includes(lower) ||
        v.contactPerson?.toLowerCase().includes(lower)
    );

    setFilteredVendors(filtered);
  }, [searchTerm, vendors]);

  /* ================= PAGINATION ================= */
  const renderPagination = () => {
    const items = [];
    for (let i = 1; i <= totalPages; i++) {
      items.push(
        <Pagination.Item
          key={i}
          active={i === currentPage}
          onClick={() => setCurrentPage(i)}
        >
          {i}
        </Pagination.Item>
      );
    }
    return items;
  };

  return (
    <div style={{ background: "#fff", minHeight: "100vh" }}>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* ===== HEADER ===== */}
      <Card className="border-0 mb-4">
        <Card.Body className="p-0">
          <Row className="align-items-center g-3 justify-content-between">
            {/* TITLE */}
            <Col md={6}>
              <h4 className="fw-bold mb-0">Vendor Management</h4>
              <small className="text-muted">
                View vendors and their supplied materials
              </small>
            </Col>

            {/* SEARCH */}
            <Col md={6}>
              <div className="d-flex">
                <InputGroup>
                  <InputGroup.Text>
                    <Search />
                  </InputGroup.Text>
                  <Form.Control
                    placeholder="Search by company, email or contact"
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                  />
                </InputGroup>
                <Button
                  variant="outline-primary"
                  onClick={() => {
                    setSearchTerm("");
                    setCurrentPage(1);
                    fetchVendors();
                  }}
                  title="Refresh"
                  className="ms-2"
                >
                  <ArrowClockwise />
                </Button>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* ===== TABLE ===== */}
      <Card className="border-0 shadow-sm">
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner />
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <Table hover className="mb-0">
                  <thead className="table-dark">
                    <tr>
                      <th>Company</th>
                      <th>Contact Person</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredVendors.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="text-center py-4 text-muted">
                          No vendors found
                        </td>
                      </tr>
                    ) : (
                      filteredVendors.map((v, idx) => (
                        <tr key={idx}>
                          <td className="fw-semibold">{v.companyName}</td>
                          <td>{v.contactPerson || "-"}</td>
                          <td>
                            <Envelope className="me-1" />
                            {v.email}
                          </td>
                          <td>
                            <Telephone className="me-1" />
                            {v.phone || "-"}
                          </td>
                          <td>
                            <Button
                              size="sm"
                              variant="primary"
                              onClick={() => {
                                setSelectedVendor(v);
                                setShowView(true);
                              }}
                            >
                              <Eye />
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>

              {totalPages > 1 && (
                <div className="p-3 d-flex justify-content-end">
                  <Pagination className="mb-0">{renderPagination()}</Pagination>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* ===== VIEW MODAL ===== */}
      <Modal
        show={showView}
        onHide={() => setShowView(false)}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Vendor Details – {selectedVendor?.companyName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedVendor && (
            <>
              <Row className="mb-4">
                <Col md={6}>
                  <p>
                    <Envelope className="me-2" />
                    {selectedVendor.email}
                  </p>
                  <p>
                    <Telephone className="me-2" />
                    {selectedVendor.phone || "N/A"}
                  </p>
                </Col>
                <Col md={6}>
                  <Badge bg="info">
                    Contact: {selectedVendor.contactPerson || "N/A"}
                  </Badge>
                </Col>
              </Row>

              {selectedVendor.projects?.length > 0 ? (
                selectedVendor.projects.map((project, pIdx) => (
                  <Card key={pIdx} className="mb-3">
                    <Card.Body>
                      <h6 className="fw-bold mb-2">{project.projectName}</h6>

                      {project.tasks?.length > 0 ? (
                        <Table bordered size="sm" className="mt-3">
                          <thead>
                            <tr>
                              <th>Task</th>
                              <th>Rate / Sqft</th>
                              <th>Total Sqft</th>
                              <th>Total Amount</th>
                              <th>Actual Cost</th>
                              <th>Created</th>
                            </tr>
                          </thead>
                          <tbody>
                            {project.tasks.map((task, tIdx) => (
                              <tr key={tIdx}>
                                <td>{task.taskName || "-"}</td>
                                <td>₹{task.ratePerSqft || "-"}</td>
                                <td>{task.totalSqft || "-"}</td>
                                <td>₹{task.totalAmount || "-"}</td>
                                <td>₹{task.actualCost || 0}</td>
                                <td>
                                  <Calendar className="me-1" />
                                  {task.createdAt
                                    ? new Date(
                                        task.createdAt
                                      ).toLocaleDateString()
                                    : "-"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      ) : (
                        <p className="text-muted mt-2">No tasks found</p>
                      )}
                    </Card.Body>
                  </Card>
                ))
              ) : (
                <p className="text-muted">No projects assigned</p>
              )}
            </>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default VendorManagement;
